import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import AdminDashboard from "./pages/AdminDashboard";
import AdminOnboardingView from "./pages/AdminOnboardingView";
import AdminSettings from "./pages/AdminSettings";
import AdminAuditLog from "./pages/AdminAuditLog";
import AdminSecurityEvents from "./pages/AdminSecurityEvents";
import AdminImportITGlue from "./pages/AdminImportITGlue";
import AdminClients from "./pages/AdminClients";
import ClientPortal from "./pages/ClientPortal";
import ClientWizard from "./pages/ClientWizard";
import { getToken } from "./lib/api";
import "./App.css";

// JWT payload is a Base64URL-encoded JSON blob between the two dots. Reading
// it client-side is purely for routing/UX — the server independently verifies
// the signature on every API call, so a forged token here grants nothing.
function readJwtPayload() {
  try {
    const t = getToken();
    if (!t) return null;
    const body = t.split(".")[1];
    if (!body) return null;
    const padded = body.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(body.length / 4) * 4, "=");
    return JSON.parse(atob(padded));
  } catch { return null; }
}

const Require = ({ children }) => (getToken() ? children : <Navigate to="/login" replace />);

// Admin-only route guard: if signed in as client, bounce them to /portal so
// they only ever see their own data. Unauthenticated → /login.
const RequireAdmin = ({ children }) => {
  if (!getToken()) return <Navigate to="/login" replace />;
  const p = readJwtPayload();
  if (p?.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
};

// Client-only route guard: admins bypass to their dashboard; unauth → login.
const RequireClient = ({ children }) => {
  if (!getToken()) return <Navigate to="/login" replace />;
  const p = readJwtPayload();
  if (p?.role === "admin") return <Navigate to="/admin" replace />;
  return children;
};

export default function App() {
  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=IBM+Plex+Sans:wght@400;500;600&family=Dancing+Script:wght@500;700&display=swap";
    document.head.appendChild(l);
  }, []);
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace/>}/>
          <Route path="/login" element={<Login/>}/>
          <Route path="/auth/callback" element={<AuthCallback/>}/>
          <Route path="/admin" element={<RequireAdmin><AdminDashboard/></RequireAdmin>}/>
          <Route path="/admin/settings" element={<RequireAdmin><AdminSettings/></RequireAdmin>}/>
          <Route path="/admin/audit-log" element={<RequireAdmin><AdminAuditLog/></RequireAdmin>}/>
          <Route path="/admin/security-events" element={<RequireAdmin><AdminSecurityEvents/></RequireAdmin>}/>
          <Route path="/admin/import-itglue" element={<RequireAdmin><AdminImportITGlue/></RequireAdmin>}/>
          <Route path="/admin/clients" element={<RequireAdmin><AdminClients/></RequireAdmin>}/>
          <Route path="/admin/onboarding/:id" element={<RequireAdmin><AdminOnboardingView/></RequireAdmin>}/>
          <Route path="/admin/onboarding/:id/edit" element={<RequireAdmin><ClientWizard/></RequireAdmin>}/>
          <Route path="/admin/onboarding/:id/preview" element={<RequireAdmin><ClientWizard/></RequireAdmin>}/>
          <Route path="/portal" element={<RequireClient><ClientPortal/></RequireClient>}/>
          {/* JWT-mode wizard works for both roles (admin can fill on behalf, client can resume) */}
          <Route path="/onboarding/jwt/:jwtOnbId" element={<Require><ClientWizard/></Require>}/>
          {/* Magic-link token wizard is public-by-design — the token IS the auth */}
          <Route path="/onboarding/:token" element={<ClientWizard/>}/>
        </Routes>
      </BrowserRouter>
      <Toaster theme="light" position="top-right"/>
    </>
  );
}
